import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  Bell,
  Filter,
  Search,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import { useAlertStore } from '../stores/alertStore'
import { AnalystNavigation } from '../components/Navigation'
import { AlertTimeline } from '../components/AlertTimeline'
import { ThisPollForwardEvidence } from '../components/ThisPollForwardEvidence'
import { ThisPollOauthGrantEvidence } from '../components/ThisPollOauthGrantEvidence'
import { cn } from '../lib/utils'
import type { Alert } from '../types'

type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low'
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'dismissed'

const severityOptions: { value: SeverityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'dismissed', label: 'Dismissed' },
]

export function AlertsPage() {
  const { alerts, fetchAlerts, isLoading, error } = useAlertStore()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await fetchAlerts()
    setIsRefreshing(false)
  }, [fetchAlerts])

  // Filter alerts
  const filteredAlerts = alerts.filter((alert: Alert) => {
    if (severityFilter !== 'all' && alert.severity !== severityFilter) return false
    if (statusFilter !== 'all' && alert.status !== statusFilter) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return alert.headline.toLowerCase().includes(q)
    }
    return true
  })

  const stats = {
    total: alerts.length,
    pending: alerts.filter((a) => a.status === 'pending').length,
    critical: alerts.filter((a) => a.status === 'pending' && a.severity === 'critical').length,
    high: alerts.filter((a) => a.status === 'pending' && a.severity === 'high').length,
  }

  return (
    <div className="min-h-screen bg-cream">
      <AnalystNavigation />

      <main className="max-w-5xl mx-auto px-6 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brass/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-brass" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-xl font-serif font-bold text-navy">Alert History</h1>
                <p className="text-sm text-gray-500">
                  {stats.pending} pending &middot; {stats.critical} critical &middot; {stats.high} high
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              className="tap-min flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} aria-hidden="true" />
              Refresh
            </button>
          </div>
        </motion.div>

        {/* Stats cards */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Alerts', value: stats.total, color: 'text-navy', bg: 'bg-gray-100' },
            { label: 'Pending', value: stats.pending, color: 'text-warning', bg: 'bg-warning/10' },
            { label: 'Critical', value: stats.critical, color: 'text-danger', bg: 'bg-danger/10' },
            { label: 'High', value: stats.high, color: 'text-brass', bg: 'bg-brass/10' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl border border-gray-200 p-4"
            >
              <p className="text-2xl font-bold text-navy">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 mr-2">
              <Filter className="w-4 h-4" aria-hidden="true" />
              Filters
            </div>

            {/* Severity filter */}
            <div className="flex items-center gap-1">
              {severityOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSeverityFilter(opt.value)}
                  className={cn(
                    'tap-min px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    severityFilter === opt.value
                      ? 'bg-brass text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="tap-min px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass"
              aria-label="Filter by status"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Search */}
            <div className="relative ml-auto">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search alerts..."
                className="tap-min pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass w-56"
              />
            </div>

            {(severityFilter !== 'all' || statusFilter !== 'all' || searchQuery) && (
              <button
                type="button"
                onClick={() => {
                  setSeverityFilter('all')
                  setStatusFilter('all')
                  setSearchQuery('')
                }}
                className="tap-min px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-navy hover:bg-gray-100 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Honest load failure — a fetch that failed must never masquerade as
            the calm "All Clear" empty state. Surface it, and suppress the empty
            timeline when there is nothing that actually loaded to show. */}
        {error && (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              Couldn&rsquo;t load the alert queue ({error}). Nothing below is a
              calm all-clear — this view never treats a load failure as an empty
              queue.
            </span>
          </div>
        )}

        {/* This-poll email-rule forward evidence (DW-ITEM21-THIS-POLL-FE).
            Fetches on load; renders NOTHING when the this-poll list is empty
            (BLIND, not all-clear). Evidence only — no Approve/Reject. */}
        <ThisPollForwardEvidence />

        {/* This-poll OAuth consent-grant evidence
            (DW-ITEM21-THIS-POLL-OAUTH-GRANT-FE). Same doctrine as its
            sibling above: [] or fetch failure renders NOTHING (BLIND, not
            all-clear). Evidence only — no Approve/Reject. */}
        <ThisPollOauthGrantEvidence />

        {/* Alert timeline */}
        {error && filteredAlerts.length === 0 ? null : (
          <AlertTimeline
            alerts={filteredAlerts}
            isLoading={isLoading || isRefreshing}
            onRefresh={handleRefresh}
          />
        )}
      </main>
    </div>
  )
}
